import { FaArrowLeft } from "react-icons/fa";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useModal } from "../../context/ConfirmModalContext";
import FullPageSpinner from "../../components/FullPageSpinner";
import User from "../../models/User";
import { v4 as uuidv4 } from "uuid";
import { synchroniseStaffs, totalErnings, userConflict } from "../../utils/user-helper";
import { sendRequest } from "../../utils/api";
import NewUserModal from "../../Modal/staff/CreateUserModal";
import { initAllStores } from "../../utils/cache";
import { useToast } from "../../context/ToastContext";
import InviteModal from "../../Modal/staff/InviteModal";
import { formatDateForInput } from "../../utils/date";
import { isNumber } from "../../utils/string";
import { MAXIMUM_LEAVE_ALLOWED } from "../../constants";

// const today = new Date().toISOString().split("T")[0];

export default function CreateUser() {
  const location = useLocation();
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();

  const staffId = urlParams.get("staffId");

  const userObjString = localStorage.getItem("user");
  const user = JSON.parse(userObjString ?? "{}");

  const selfRegister = location.pathname === "/staffs/signup";
  const retrieveStaff = location.state as User;

  // ---------------------- STAFF BASIC INFO. STATE ----------------------
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");

  const [dob, setDob] = useState("");
  const [type, setType] = useState("text");

  // ---------------------- STAFF CONTACT INFO. STATE ----------------------
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [state, setState] = useState("");
  const [nationality, setNationality] = useState("");
  const [gender, setGender] = useState("");

  // ---------------------- STAFF NEXT OF KIN (NOK) INFO. STATE ----------------------
  const [nokName, setNokName] = useState("");
  const [nokPhone, setNokPhone] = useState("");
  const [nokAddress, setNokAddress] = useState("");
  const [nokEmail, setNokEmail] = useState("");
  const [nokRelation, setNokRelation] = useState("");


  // ---------------------- STAFF EMPLOYMENT INFO. STATE ----------------------
  const [role, setRole] = useState(selfRegister || user.role !== "superAdmin" ? "staff" : "");
  const [portfolio, setPortfolio] = useState("");
  const [startDate, setStartDate] = useState("");
  const [salary, setSalary] = useState("");
  const [bank, setBank] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [leaveQuota, setLeaveQuota] = useState(MAXIMUM_LEAVE_ALLOWED);

  const [isDisabled] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [password, setPassword] = useState("");

  // ---------------------- SHOW CONFIRMATION MODAL ----------------------
  const showConfirmationModal = () => {
    const missingFields: string[] = [];
    if (!firstName) missingFields.push("First Name");
    if (!lastName) missingFields.push("Last Name");
    if (!userName) missingFields.push("User Name");
    if (!phone) missingFields.push("Staff Phone Number");
    if (!gender) missingFields.push("Staff Gender");
    if (!email) missingFields.push("Satff Eamil");
    if (!role) missingFields.push("Staff Role");
    if (!portfolio && !selfRegister) missingFields.push("Staff Portfolio");

    if (missingFields.length > 0) {
      return openModal("Missing Fields", `Please fill in: ${missingFields.join(", ")}`, closeModal, false, "Close");
    }

    openModal(
      retrieveStaff
        ? "Confirm Profile Update"
        : selfRegister
          ? "Confirm Account Creation"
          : urlParams.size === 0
            ? "Confirm Staff Registration"
            : "Confirm Staff Update",

      retrieveStaff
        ? "Are you sure you want to update your profile information?"
        : selfRegister
          ? "Are you sure you want to create this account?"
          : urlParams.size === 0
            ? "Are you sure you want to continue with this staff registration?"
            : "Are you sure you want to save the changes to this staff member’s details?",

      () => {
        // if (selfRegister) createAccount();
        if (urlParams.size > 0) updateUser();
        else createUser();
      },
      true,
      "Confirm",
      "Cancel"
    );

  };

  useEffect(() => {

    const getDate = (date: string | undefined): string => {
      if (!date) return "";
      return formatDateForInput(date);
    }
    const autofillForm = async () => {
      const cache = await initAllStores();
      let staff: User | null = await cache.getBy("staffs", { id: staffId });

      if (!staff && retrieveStaff) staff = retrieveStaff;
      if (!staff && staffId) navigate("/404");

      if (staff) {
        setFirstName(staff.firstName);
        setMiddleName(staff.middleName ?? "");
        setLastName(staff.lastName);
        setGender(staff.gender);
        setDob(getDate(staff.dob));
        setUserName(staff.userName ?? "");
        setAddress(staff.address ?? "")

        setEmail(staff.email);
        setPhone(staff.phone);
        setState(staff.state ?? "");
        setNationality(staff.nationality ?? "");

        setNokName(staff.nokName ?? "");
        setNokRelation(staff.nokRelation ?? "");
        setNokEmail(staff.nokEmail ?? "");
        setNokPhone(staff.nokPhone ?? "");
        setNokAddress(staff.nokAddress ?? "");
        setRole(staff.role);
        setPortfolio(staff.portfolio);
        setStartDate(getDate(staff.startDate));
        setSalary(String(totalErnings(staff)));
        setBank(staff.bank ?? "");
        setAccountNo(staff.accountNo ?? "");
        setLeaveQuota(String(staff.leaveQuota) ?? MAXIMUM_LEAVE_ALLOWED);
      }
    }

    autofillForm();
  }, []);

  // 🔹 Detect subdomain for multi-tenant context
  const hostname = window.location.hostname; // e.g. demo-hotel.wiventory.com
  const subdomain = hostname.split('.')[0];  // e.g. demo-hotel
  // console.log("Current tenant:", subdomain);

  const userData: User & { subdomain: string } = {
    gender: gender as Gender, phone, bank,
    firstName, middleName, lastName, role,
    userName, nokName, nokAddress, nokEmail,
    id: uuidv4(), email, portfolio, startDate,
    nokRelation, accountNo, state, nationality,
    nokPhone, address, salary: parseFloat(salary),
    subdomain, dob, leaveQuota: parseInt(leaveQuota),
  };

  const createUser = async () => {
    const cache = await initAllStores();
    setLoading(true);
    const conflict = await userConflict(
      userData.userName ?? "", userData.email, userData.id
    );
    if (conflict.status === "conflict") {
      setLoading(false);
      return openModal(
        "User Exist Already",
        conflict.message,
        closeModal, false, "Close"
      );
    }

    try {
      const data = await sendRequest("POST", "/users", userData);
      const { data: { password } } = data;
      if (selfRegister) await cache.saveOne("staffs", userData);
      setPassword(password);
      setShowModal(true);
    } catch (error: any) {
      console.log(error)
      if (error.status === 409) {
        return openModal(
          "User Exist Already",
          error.response.data.error,
          closeModal, false, "Close"
        )
      }
      showToast("Staff account creation failed. Plese try again", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async () => {
    const cache = await initAllStores();
    setLoading(true);
    const conflict = await userConflict(
      userData.userName ?? "", userData.email, staffId ?? ""
    );
    if (conflict.status === "conflict") {
      setLoading(false);
      return openModal(
        "User Exist Already",
        conflict.message,
        closeModal, false, "Close"
      );
    }
    try {
      const { id, ...staffNewData } = userData;

      await sendRequest("PUT", "/users", { staffId, staffNewData });
      if (retrieveStaff) {
        await cache.updateManyBy(
          "data", ["user"], { ...userData, id: "user", userId: retrieveStaff.id }
        );
      }
      else if (staffId) await cache.updateManyBy("staffs", [staffId], staffNewData);

      showToast(`${retrieveStaff ? "Profile" : "User"} updated successfully.`, { type: "success" });
      // setShowModal(true);
    } catch (error: any) {
      if (error.status === 409) {
        return openModal(
          "User Exist Already",
          error.response.data.error,
          closeModal, false, "Close"
        )
      }
      if (error.status === 404) synchroniseStaffs();
      showToast("Failed to update user. Please try again.", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 space-y-8 overflow-y-auto p-6">
      <FullPageSpinner show={loading} />

      {/* Invite Modal */}
      <InviteModal
        show={showInviteModal}
        onClose={() => setShowInviteModal(false)}
      />

      {showModal && (
        <NewUserModal
          email={userData.email}
          username={userData.userName}
          tempPassword={password}
          onClose={() => setShowModal(false)}
          isSelfRegister={selfRegister}
          onRedirectToLogin={() => navigate("/login")}
        />
      )}
      {/* Header with Back Arrow and Switch Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {!selfRegister && <button
            // onClick={() => navigate("/staffs")}
            onClick={() => window.history.back()}
            className="p-2 rounded-full hover:bg-gray-100 transition"
          >
            <span className="w-5 h-5 text-gray-700">{FaArrowLeft({})}</span>
          </button>}
          <div>
            <h1 className="text-2xl font-bold text-blue-600">
              {retrieveStaff ? (
                <span>👤 Update My Profile</span>
              ) : selfRegister ? (
                <span>🆕 Staff Account Registration</span>
              ) : urlParams.size < 1 ? (
                <span>✅ Create Staff</span>
              ) : (
                <span>✏️ Edit Staff</span>
              )}

            </h1>
            <p className="text-gray-600">
              {retrieveStaff ? (
                <span>Update your personal information below.</span>
              ) : selfRegister ? (
                <span>Fill in your details to create your account.</span>
              ) : urlParams.size < 1 ? (
                <span>Fill in the details to add a new staff member.</span>
              ) : (
                <span>Update the details of this staff member.</span>
              )}

            </p>
          </div>
        </div>
        {urlParams.size === 0 && !selfRegister && (<button
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          onClick={() => setShowInviteModal(true)}
        >
          Send Invite Link
        </button>)}
      </div>

      {/* Basic Staff Information */}
      <div className="bg-white shadow-md rounded-2xl p-6 space-y-6">
        <h2 className="text-lg font-semibold">🧑 Basic Identification</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              First Name
            </label>
            <input
              type="text"
              placeholder="Enter first name"
              onChange={(e) => setFirstName(e.target.value.trim())}
              value={firstName}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Middle Name (Option)
            </label>
            <input
              type="text"
              placeholder="Enter Middle Name"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value.trim())}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Last Name
            </label>
            <input
              type="text"
              placeholder="Enter Last Name"
              onChange={(e) => setLastName(e.target.value.trim())}
              value={lastName}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Gender
            </label>

            <select
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              onChange={(e) => setGender(e.target.value.trim())}
              disabled={isDisabled}
              value={gender}
            >
              <option value=""> Select Gender </option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Date of Birth
            </label>
            <input
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              type={type}
              placeholder="Enter Date of Birth"
              onFocus={() => setType("date")}
              onBlur={(e) => !e.target.value && setType("text")}
              onChange={(e) => setDob(e.target.value.trim())}
              value={dob}
            // className="form-control"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              User Name
            </label>
            <input
              type="text"
              placeholder="Enter User Name"
              onChange={(e) => setUserName(e.target.value.trim())}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              value={userName}
              disabled={isDisabled}
            />
          </div>

        </div>
      </div>

      {/* Contact Details */}
      <div className="bg-white shadow-md rounded-2xl p-6 space-y-6">
        <h2 className="text-lg font-semibold">📞 Contact Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Phone Number
            </label>
            <input
              type="tel"
              placeholder="Enter Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value.trim())}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Email
            </label>
            <input
              type="email"
              placeholder="Enter Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Address (Optional)
            </label>
            <input
              type="text"
              placeholder="Enter Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              State (Optional)
            </label>
            <input
              type="text"
              value={state}
              placeholder="Enter State"
              onChange={(e) => setState(e.target.value.trim())}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Nationality (Optional)
            </label>
            <input
              type="text"
              placeholder="Enter Nationality"
              value={nationality}
              onChange={(e) => setNationality(e.target.value.trim())}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>
        </div>
      </div>

      {/* Next of Kin (NOK) */}
      <div className="bg-white shadow-md rounded-2xl p-6 space-y-6">
        <h2 className="text-lg font-semibold">🧑‍🤝‍🧑 Next of Kin (NOK)</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Full Name (Optional)
            </label>
            <input
              type="text"
              placeholder="Full Name (Optional)"
              onChange={(e) => setNokName(e.target.value)}
              value={nokName}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Relationship  (Optional)
            </label>
            <input
              placeholder="Enter Relationship"
              className="p-3 border rounded-lg text-gray-700 focus:ring-0"
              value={nokRelation}
              onChange={(e) => setNokRelation(e.target.value.trim())}
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Phone Number (Optional)
            </label>
            <input
              type="tel"
              placeholder="Enter Phone Number"
              value={nokPhone}
              onChange={(e) => setNokPhone(e.target.value.trim())}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Enter Email (Optional)
            </label>
            <input
              type="email"
              placeholder="Enter Email Address"
              value={nokEmail}
              onChange={(e) => setNokEmail(e.target.value.trim())}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Address (Optional)
            </label>
            <input
              type="text"
              placeholder="Enter Address"
              value={nokAddress}
              onChange={(e) => setNokAddress(e.target.value)}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>
        </div>
      </div>

      {/* 👔 Employment Details */}
      {(!selfRegister && !retrieveStaff) && <div className="bg-white shadow-md rounded-2xl p-6 space-y-6">
        <h2 className="text-lg font-semibold">👔 Employment Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Role
            </label>
            <select
              className={
                `p-3 border rounded-lg focus:ring focus:ring-blue-300 
              ${selfRegister || user.role !== "superAdmin" ? "bg-gray-100 cursor-not-allowed opacity-70" : ""
                }`
              }
              onChange={(e) => setRole(e.target.value.trim())}
              disabled={selfRegister || user.role !== "superAdmin"}
              value={role}
            >
              <option value="">Select Role</option>
              {user.role === "superAdmin" && <option value="admin">Admin</option>}
              <option value="staff">Staff</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Job Role (Optional)
            </label>
            <input
              placeholder="Enter Job Role"
              className="p-3 border rounded-lg text-gray-700 focus:ring-0"
              value={portfolio}
              onChange={(e) => setPortfolio(e.target.value)}
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Start Date (Optional)
            </label>
            <input
              type={type}
              placeholder="Enter Employment Start Date"
              onChange={(e) => setStartDate(e.target.value.trim())}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              onFocus={() => setType("date")}
              value={startDate}
              onBlur={(e) => !e.target.value && setType("text")}
              disabled={isDisabled}
            />
          </div>
          {/* <input
            type="text"
            placeholder="Salary (Optional)"
            value={salary}
            onChange={(e) => setSalary(e.target.value)}
            className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
            disabled={isDisabled}
          /> */}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Bank (Optional)
            </label>
            <input
              type="text"
              placeholder="Enter Bank Name"
              value={bank}
              onChange={(e) => setBank(e.target.value)}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Account Number (Optional)
            </label>
            <input
              type="text"
              placeholder="Enter Account Number"
              value={accountNo}
              onChange={(e) => setAccountNo(e.target.value.trim())}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-600">
              Maximum Leave Days Allowed
            </label>
            <input
              type="text"
              placeholder="Ente Maximum Leave Days Allowed"
              value={leaveQuota}
              onChange={(e) => {
                const leave = e.target.value;
                if (isNumber(leave)) setLeaveQuota(leave.trim());
              }}
              className="p-3 border rounded-lg focus:ring focus:ring-blue-300"
              disabled={isDisabled}
            />
          </div>
        </div>
      </div>}
      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          onClick={() => showConfirmationModal()}
          className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow hover:bg-blue-700 transition"
        >
          {retrieveStaff ? (
            <span>Update Profile</span>
          ) : selfRegister ? (
            <span>Create Account</span>
          ) : urlParams.size < 1 ? (
            <span>Add Staff</span>
          ) : (
            <span>Update Staff</span>
          )}

        </button>
      </div>
    </div>
  );
}